import {
    Card,
    Typography,
    Box,
    Grid,
    LinearProgress,
} from "@mui/material";
import { FaCheckCircle, FaTimesCircle } from "react-icons/fa";
import COLORS from "../../../../../constants/colors";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';


const formatCurrency = (amount) => {
    if (isNaN(amount) || amount === null || amount === undefined) return "UGX 0";
    const roundedAmount = Math.round(amount);
    return `UGX ${roundedAmount.toLocaleString("en-UG")}`;
};

// Helper function to truncate text
const truncateText = (text, wordLimit = 20) => {
  if (!text) return "";
  const words = text.split(/\s+/);
  if (words.length <= wordLimit) return words.join(" ");
  return words.slice(0, wordLimit).join(" ") + "...";
};

// Individual Campaign Item Component remains the same
const CampaignItem = ({ campaign, isOpen }) => {
    const percentage = campaign.goal > 0 ? (campaign.raised / campaign.goal) * 100 : 0;
    const progressBarColor = isOpen ? (percentage >= 100 ? "success" : "primary") : "error";

    return (
        <Box
            sx={{
                mb: 2.5,
                p: 2.5,
                borderRadius: 2,
                backgroundColor: "#fefefe",
                border: "1px solid #eee",
                transition: "box-shadow 0.3s ease-in-out",
                "&:last-child": {
                    mb: 0,
                    borderBottom: "none",
                },
                "&:hover": {
                    boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
                },
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
                {isOpen ? (
                    <FaCheckCircle color="#4caf50" size={20} />
                ) : (
                    <FaTimesCircle color="#f44336" size={20} />
                )}
                <Typography variant="body1" fontWeight="bold" sx={{ color: "#333", fontSize: { xs: '0.95rem', sm: '1.05rem' } }}>
                    {campaign.name}
                </Typography>
            </Box>

                                      <Box
  sx={{
    color: COLORS.textSecondary,
    "& p:last-child": { mb: 0 },
    "& ul, & ol": { pl: 2 },
    textAlign: "left",
    lineHeight: 1.5, // <-- reduce line spacing
  }}
>
  <ReactMarkdown
    remarkPlugins={[remarkGfm]}
    rehypePlugins={[rehypeRaw]}
  >
    {truncateText(campaign.description, 20)}
  </ReactMarkdown>
</Box>

            <Typography variant="body2" sx={{ mb: 1 }}>
                <Typography component="span" fontWeight="bold">Raised:</Typography>{" "}
                <Typography component="span" sx={{ color: COLORS.primary, fontWeight: 'bold' }}>
                    {formatCurrency(campaign.raised)}
                </Typography>{" "}
                of{" "}
                <Typography component="span" fontWeight="bold">
                    {formatCurrency(campaign.goal)}
                </Typography>
            </Typography>

            <LinearProgress
                variant="determinate"
                value={percentage > 100 ? 100 : percentage}
                sx={{
                    mt: 1,
                    height: 8,
                    borderRadius: 5,
                    backgroundColor: "#e0e0e0",
                    "& .MuiLinearProgress-bar": {
                        backgroundColor: isOpen && percentage >= 100 ? '#4caf50' : COLORS.primary,
                    },
                }}
            />
            {isOpen && (
                <Typography variant="caption" sx={{ mt: 0.5, display: 'block', textAlign: 'right', color: COLORS.primary, fontWeight: 'bold' }}>
                    {percentage.toFixed(1)}% Raised
                </Typography>
            )}
        </Box>
    );
};

// The main CampaignsOverview component
// It now receives 'campaigns' as a prop instead of fetching it
const CampaignsOverview = ({ campaigns }) => {
    const openCampaigns = campaigns.filter((campaign) => campaign.status === "open");
    const closedCampaigns = campaigns.filter((campaign) => campaign.status === "closed");

    return (
        <Grid container spacing={3}>
            {/* Open Campaigns */}
            <Grid item xs={12} sm={6}>
                <Card
                    sx={{
                        p: { xs: 2, md: 3 },
                        borderRadius: 2,
                        backgroundColor: "white",
                        color: "#333",
                        boxShadow: 0,
                        height: "100%",
                        display: 'flex',
                        flexDirection: 'column',
                        border: `1px solid ${COLORS.border}`,
                    }}
                >
                    <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, textAlign: "left", color: COLORS.primary }}>
                        Active Campaigns ({openCampaigns.length})
                    </Typography>
                    {openCampaigns.length > 0 ? (
                        openCampaigns.map((campaign) => (
                            <CampaignItem key={campaign.id} campaign={campaign} isOpen={true} />
                        ))
                    ) : (
                        <Typography variant="body2" sx={{ color: "#666", textAlign: "center", mt: 2, p: 2, fontStyle: 'italic' }}>
                            No active campaigns at the moment.
                        </Typography>
                    )}
                </Card>
            </Grid>

            {/* Closed Campaigns */}
            <Grid item xs={12} sm={6}>
                <Card
                    sx={{
                        p: { xs: 2, md: 3 },
                        borderRadius: 2,
                        backgroundColor: "white",
                        color: "#333",
                        boxShadow: 0,
                        height: "100%",
                        display: 'flex',
                        flexDirection: 'column',
                        border: `1px solid ${COLORS.border}`,
                    }}
                >
                    <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, textAlign: "left", color: "#f44336" }}>
                        Closed Campaigns ({closedCampaigns.length})
                    </Typography>
                    {closedCampaigns.length > 0 ? (
                        closedCampaigns.map((campaign) => (
                            <CampaignItem key={campaign.id} campaign={campaign} isOpen={false} />
                        ))
                    ) : (
                        <Typography variant="body2" sx={{ color: "#666", textAlign: "center", mt: 2, p: 2, fontStyle: 'italic' }}>
                            No campaigns have closed yet.
                        </Typography>
                    )}
                </Card>
            </Grid>
        </Grid>
    );
};

export default CampaignsOverview;